import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, TextField } from "@mui/material";
import {
  Button,
  Iconography,
  IconographySizes,
  Toggle,
  ToggleSizes,
} from "@hid-galaxy-ui/galaxy-react";
import "../../validation-management/Modals.scss";
import DescriptionEditorField from "../components/DescriptionEditorField";
import { isRequiredNameFilled } from "../components/formValidation";

export type TechnologyFormData = {
  name: string;
  description: string;
  status: boolean;
};

type Props = {
  open: boolean;
  mode: "add" | "edit";
  initialData?: TechnologyFormData | null;
  isSubmitting?: boolean;
  onClose: () => void;
  onSubmit: (data: TechnologyFormData) => void;
};

const AddTechnologyModal: React.FC<Props> = ({
  open,
  mode,
  initialData,
  isSubmitting = false,
  onClose,
  onSubmit,
}) => {
  const [formData, setFormData] = useState<TechnologyFormData>({
    name: "",
    description: "",
    status: true,
  });

  useEffect(() => {
    if (open) {
      setFormData(
        initialData || {
          name: "",
          description: "",
          status: true,
        }
      );
    }
  }, [open, initialData]);

  const isFormValid = useMemo(
    () => isRequiredNameFilled(formData.name),
    [formData.name]
  );

  const handleSubmit = () => {
    if (!isFormValid || isSubmitting) return;
    onSubmit(formData);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      className="requirement-modal technology-management-modal"
    >
      <div className="benefits-ptv-modal tm-modal">
        <header className="benefits-ptv-modal-header">
          <div>
            <p className="benefits-ptv-modal-eyebrow">Technology Management</p>
            <h3>{mode === "add" ? "Add New Technology" : "Edit Technology"}</h3>
            <p className="benefits-ptv-modal-subtitle">Configure a new technology</p>
          </div>
          <button
            type="button"
            className="benefits-ptv-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            <Iconography icon="xmark" size={IconographySizes.Medium} />
          </button>
        </header>
        <DialogContent className="benefits-ptv-modal-content">
          <div className="tm-form-row">
            <div className="form-field">
              <label htmlFor="technology-name">Technology Name*</label>
              <TextField
                id="technology-name"
                fullWidth
                variant="outlined"
                placeholder="e.g., Technical Excellence"
                value={formData.name}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, name: e.target.value }))
                }
              />
            </div>
            <div className="form-field tm-toggle-field">
              <label htmlFor="technology-status">Technology Status*</label>
              <div className="tm-toggle-row">
                <Toggle
                  id="technology-status"
                  labelText=""
                  labelON=""
                  labelOFF=""
                  toggled={formData.status}
                  onClick={() =>
                    setFormData((prev) => ({ ...prev, status: !prev.status }))
                  }
                  size={ToggleSizes.Mini}
                />
                <span>{formData.status ? "Active" : "Inactive"}</span>
              </div>
            </div>
          </div>
          <DescriptionEditorField
            id="technology-description"
            placeholder="Enter technology description"
            value={formData.description}
            onChange={(description) =>
              setFormData((prev) => ({ ...prev, description }))
            }
          />
        </DialogContent>
        <footer className="benefits-ptv-modal-footer">
          <Button variant="secondary" label="CANCEL" onClick={onClose} />
          <Button
            variant="primary"
            label={mode === "add" ? "ADD TECHNOLOGY" : "SAVE TECHNOLOGY"}
            onClick={handleSubmit}
            disabled={!isFormValid || isSubmitting}
          />
        </footer>
      </div>
    </Dialog>
  );
};

export default AddTechnologyModal;
